import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type FeedbackStatus = "new" | "acknowledged" | "closed" | "deleted";

export interface FeedbackReply {
  id: string;
  body: string;
  created_at: string;
  authorName: string;
}

export interface MyFeedbackRow {
  id: string;
  type: string;
  body: string;
  page_url: string | null;
  status: FeedbackStatus;
  created_at: string;
  handled_at: string | null;
  replies: FeedbackReply[];
}

interface RawReply {
  id: string;
  body: string;
  created_at: string;
  author: { full_name: string | null } | null;
}
interface RawRow {
  id: string; type: string; body: string; page_url: string | null;
  status: FeedbackStatus; created_at: string; handled_at: string | null;
  feedback_replies: RawReply[] | null;
}

/** Ren mapper (testbar uten React): rå rader → UI-rader med svar. */
export function mapFeedbackRows(rows: RawRow[]): MyFeedbackRow[] {
  return rows.map((r) => ({
    id: r.id, type: r.type, body: r.body, page_url: r.page_url,
    status: r.status, created_at: r.created_at, handled_at: r.handled_at,
    replies: (r.feedback_replies ?? [])
      .slice()
      .sort((a, b) => a.created_at.localeCompare(b.created_at))
      .map((rep) => ({
        id: rep.id, body: rep.body, created_at: rep.created_at,
        authorName: rep.author?.full_name ?? "Erik",
      })),
  }));
}

export function useMyFeedback(userId: string | null) {
  return useQuery({
    queryKey: ["my-feedback", userId],
    enabled: !!userId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<MyFeedbackRow[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("feedback")
        .select(
          "id, type, body, page_url, status, created_at, handled_at, feedback_replies(id, body, created_at, author:author_id(full_name))",
        )
        .eq("user_id", userId)
        .neq("status", "deleted")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return mapFeedbackRows((data ?? []) as unknown as RawRow[]);
    },
  });
}
